import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Sobreposicao } from '@/componentes/JanelaDeAcao';
import { Seletor } from '@/componentes/Seletor';
import { formatarInteiro } from '@/lib/formato';
import { retornoDe } from '@/lib/retorno';

import { api, type Inspecao, type ItemDaInspecao } from './api';
import {
  corpoDaResposta,
  criticidadeEscrita,
  porqueNaoConclui,
  rascunhoDe,
  respostaEscrita,
  type CorpoDaResposta,
  type EscolhaDoItem,
  type RascunhoDoItem,
} from './inspecao';

/**
 * RESPONDER A INSPEÇÃO NA MESA (achado dos robôs no QAS, 20/10/2026).
 *
 * A mesa abria a inspeção e não tinha onde responder os itens: o "Concluir"
 * recusava com "1 item ainda não foi respondido" e a inspeção ficava em
 * andamento para sempre. O celular responde no checklist do campo (M20); aqui
 * cada item se responde na hora, pela mesma rota de respostas, com as mesmas
 * três saídas — conforme, não conforme, não se aplica — e o não conforme pede
 * o que foi visto, como no celular (a foto se anexa depois na ocorrência).
 *
 * É GAVETA, e não modal (p.29): a lista do checklist passa de seis campos.
 * O primário da gaveta é o "Concluir inspeção", que só se liga com todo item
 * respondido e diz, enquanto isso, quantos faltam.
 */
export function RespostasDaInspecao({
  inspecao,
  aoFechar,
  aoConcluir,
  concluindo,
}: {
  readonly inspecao: Inspecao | null;
  readonly aoFechar: () => void;
  readonly aoConcluir: (id: string) => void;
  readonly concluindo: boolean;
}) {
  const cliente = useQueryClient();
  const itens = useQuery({
    queryKey: ['inspecao', 'respostas', inspecao?.id ?? null],
    queryFn: () => api.itensDaInspecao((inspecao as Inspecao).id),
    enabled: inspecao !== null,
  });
  /* O que a pessoa está escrevendo e ainda não gravou, por item. */
  const [rascunhos, setRascunhos] = useState<Readonly<Record<string, RascunhoDoItem>>>({});

  const responder = useMutation({
    mutationFn: (p: { readonly corpo: CorpoDaResposta; readonly item: ItemDaInspecao; readonly escrita: string }) =>
      api.responder((inspecao as Inspecao).id, p.corpo),
    meta: {
      retorno: retornoDe<{ ocorrencia: { criticidade: string } | null }, { item: ItemDaInspecao; escrita: string }>((r, p) => ({
        titulo: 'Item da inspeção respondido',
        tom: r.ocorrencia ? 'ressalva' : 'sucesso',
        ...(inspecao ? { alvo: { codigo: inspecao.equipamento, nome: inspecao.checklist } } : {}),
        fatos: [`${formatarInteiro(p.item.ordem)}. ${p.item.descricao}`, p.escrita],
        ...(r.ocorrencia ? { efeito: 'Ocorrência aberta na fila de ocorrências' } : {}),
      })),
    },
    onSuccess: async (_r, p) => {
      setRascunhos((agora) => {
        const copia = { ...agora };
        delete copia[p.item.id];
        return copia;
      });
      await cliente.invalidateQueries({ queryKey: ['inspecao'] });
    },
  });

  const lista = itens.data?.itens ?? [];
  const motivo = itens.data ? porqueNaoConclui(lista) : null;
  const rascunhoDo = (item: ItemDaInspecao) => rascunhos[item.id] ?? rascunhoDe(item);
  const mudar = (item: ItemDaInspecao, parte: Partial<RascunhoDoItem>) =>
    setRascunhos((agora) => ({ ...agora, [item.id]: { ...rascunhoDo(item), ...parte } }));

  /* Grava o item com o rascunho dado — o clique em "Conforme" e em "Não se
     aplica" grava na hora; o resto grava no botão do item. */
  const gravar = (item: ItemDaInspecao, rascunho: RascunhoDoItem) => {
    const r = corpoDaResposta(item, rascunho);
    if ('falta' in r) return;
    const escrita = respostaEscrita({ ...item, resposta: {
      conforme: r.corpo.conforme ?? null,
      naoSeAplica: r.corpo.naoSeAplica ?? false,
      medida: r.corpo.medida ?? null,
      texto: r.corpo.texto ?? '',
    } }) ?? '';
    responder.mutate({ corpo: r.corpo, item, escrita });
  };

  const fechar = () => {
    setRascunhos({});
    aoFechar();
  };

  return (
    <Sobreposicao
      lugar="direita"
      titulo="Responder a inspeção"
      {...(inspecao ? { descricao: `${inspecao.equipamento} · ${inspecao.checklist}` } : {})}
      aberta={inspecao !== null}
      aoFechar={fechar}
      acoes={
        <>
          {motivo ? <span className="txt-apoio mr-auto text-pretty text-tinta-secundaria">{motivo}</span> : null}
          <Botao nivel="terciario" onClick={fechar}>
            Voltar
          </Botao>
          <Botao
            nivel="primario"
            disabled={itens.data === undefined || motivo !== null || concluindo || responder.isPending}
            onClick={() => inspecao && aoConcluir(inspecao.id)}
          >
            {concluindo ? 'Concluindo…' : 'Concluir inspeção'}
          </Botao>
        </>
      }
    >
      {itens.isPending ? (
        <Carregando oQue="Carregando os itens da inspeção" className="flex flex-col gap-4">
          <Barra className="h-16 w-full rounded-card" />
          <Barra className="h-16 w-full rounded-card" />
          <Barra className="h-16 w-full rounded-card" />
        </Carregando>
      ) : itens.isError ? (
        <ErroDaLista oQue="Os itens da inspeção" erro={itens.error} aoTentarDeNovo={() => void itens.refetch()} />
      ) : lista.length === 0 ? (
        <EstadoVazio
          titulo="O checklist desta inspeção não tem itens"
          descricao="Acrescente os itens ao checklist em Montar checklists e publique uma revisão; esta inspeção não tem o que responder."
        />
      ) : (
        <ol className="flex flex-col gap-4">
          {lista.map((item) => {
            const rascunho = rascunhoDo(item);
            const gravada = respostaEscrita(item);
            const pronto = corpoDaResposta(item, rascunho);
            const ocupado = responder.isPending && responder.variables.item.id === item.id;
            return (
              <li key={item.id} className="flex flex-col gap-3 rounded-card border border-borda-sutil bg-card p-4">
                <div className="flex flex-col gap-1">
                  <span className="txt-corpo font-semibold text-tinta">{`${formatarInteiro(item.ordem)}. ${item.descricao}`}</span>
                  <span className="txt-apoio text-tinta-secundaria">
                    {[item.grupo || null, criticidadeEscrita(item.criticidade)].filter(Boolean).join(' · ')}
                  </span>
                  <span className="txt-apoio text-tinta-secundaria">
                    {gravada === null ? 'Ainda não respondido' : `Respondido: ${gravada}`}
                  </span>
                </div>

                {item.tipo === 'conformidade' ? (
                  <Seletor<EscolhaDoItem>
                    rotulo={`Resposta do item ${formatarInteiro(item.ordem)}`}
                    valor={rascunho.escolha ?? ('' as EscolhaDoItem)}
                    opcoes={[
                      { valor: 'conforme', rotulo: 'Conforme' },
                      { valor: 'nao_conforme', rotulo: 'Não conforme' },
                      { valor: 'nao_se_aplica', rotulo: 'Não se aplica' },
                    ]}
                    aoEscolher={(escolha) => {
                      const novo = { ...rascunho, escolha };
                      mudar(item, { escolha });
                      if (escolha !== 'nao_conforme') gravar(item, novo);
                    }}
                  />
                ) : null}

                {item.tipo === 'medida' ? (
                  <CampoTexto
                    rotulo={item.unidade ? `Medida em ${item.unidade}` : 'Medida'}
                    valor={rascunho.medida}
                    aoMudar={(medida) => mudar(item, { medida, escolha: null })}
                  />
                ) : null}

                {item.tipo === 'texto' || (item.tipo === 'conformidade' && rascunho.escolha === 'nao_conforme') ? (
                  <CampoTexto
                    rotulo={item.tipo === 'texto' ? 'Resposta' : 'O que você viu'}
                    valor={rascunho.texto}
                    aoMudar={(texto) => mudar(item, item.tipo === 'texto' ? { texto, escolha: null } : { texto })}
                    maxLength={600}
                  />
                ) : null}

                {item.tipo !== 'conformidade' || rascunho.escolha === 'nao_conforme' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Botao
                      nivel="secundario"
                      disabled={'falta' in pronto || responder.isPending}
                      onClick={() => gravar(item, rascunho.escolha === 'nao_se_aplica' ? { ...rascunho, escolha: null } : rascunho)}
                    >
                      {ocupado ? 'Gravando…' : item.tipo === 'conformidade' ? 'Gravar achado' : 'Gravar resposta'}
                    </Botao>
                    {item.tipo !== 'conformidade' ? (
                      <Botao
                        nivel="fantasma"
                        disabled={responder.isPending}
                        onClick={() => gravar(item, { ...rascunho, escolha: 'nao_se_aplica' })}
                      >
                        Não se aplica
                      </Botao>
                    ) : null}
                    {'falta' in pronto ? <span className="txt-apoio text-tinta-secundaria">{pronto.falta}</span> : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </Sobreposicao>
  );
}
